import { Extension } from '@tiptap/core';
import Placeholder from '@tiptap/extension-placeholder';
import { TableKit } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import { Plugin } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Editor as EditorSemTela, type Extensions } from '@tiptap/core';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { forwardRef, useEffect, useImperativeHandle, useState, type ReactNode } from 'react';
import { useDialogo } from './ui';
import { juntarClasses } from './ui/classes';
import '../estilos/editor-de-contrato.css';

/**
 * Editor de contrato no padrão Word (lista de 21/09).
 *
 * ## Por que um editor de texto, e não um `<textarea>` de HTML
 *
 * Quem escreve o modelo é o jurídico do fornecedor ou do assessor, e ele
 * escreve no Word. Pedir HTML era pedir que aprendesse outra língua para
 * digitar o mesmo contrato — na prática o texto vinha colado com marcação
 * quebrada. Aqui a pessoa escreve, formata e insere as variáveis como faria
 * no Word; o que o sistema guarda continua sendo HTML (`getHTML()`), e o
 * gerador de PDF não muda.
 *
 * ## O que existe na barra
 *
 * O que um contrato usa: títulos, negrito/itálico/sublinhado, alinhamento,
 * listas, tabela, linha separadora, desfazer/refazer — e "Inserir variável",
 * que coloca o `[TOKEN]` no cursor. Cor de fonte, imagem e tamanho de letra
 * ficam de fora de propósito: o PDF de contrato tem a diagramação do sistema,
 * e oferecer o que ele não respeita é prometer o que não sai.
 *
 * ## HTML de fora para dentro
 *
 * `value` é a fonte de verdade quando muda por fora (carregou o modelo,
 * importou do Word). Um `setContent` a cada tecla jogaria o cursor para o
 * fim; por isso só se aplica quando o HTML de fora difere do que o editor
 * tem.
 *
 * ## Também para os e-mails (D-017)
 *
 * `modo="email"` é o mesmo editor para os modelos de e-mail: a folha tem a
 * largura do e-mail, a letra é a dele, há link (que o contrato não tem — o PDF
 * não clica) e as variáveis são `{{chaves}}`, inseridas pelo rótulo.
 */

export type ModoDoEditor = 'contrato' | 'email';

/** As variáveis com rótulo — o menu mostra o rótulo e insere o texto. */
export interface VariavelDoEditor {
  readonly texto: string;
  readonly rotulo: string;
}

export interface EditorDeContratoHandle {
  /** Insere texto no cursor — é como o painel de variáveis entra no editor. */
  inserir(texto: string): void;
}

export interface PropsEditorDeContrato {
  readonly value: string;
  readonly onChange: (html: string) => void;
  /** Os `[TOKENS]` que o gerador conhece; viram o menu "Inserir variável". */
  readonly tokens?: readonly string[];
  /** No lugar de `tokens`: variáveis com rótulo, inseridas como estão. */
  readonly variaveis?: readonly VariavelDoEditor[];
  readonly modo?: ModoDoEditor;
  readonly placeholder?: string;
  readonly className?: string;
}

/** Destaca `[TOKEN]` (ou `{{chave}}`, no e-mail) no texto — sem mexer no HTML gravado. */
const DestaqueDeVariaveis = Extension.create<{ padrao: RegExp }>({
  name: 'destaqueDeVariaveis',
  addOptions() {
    return { padrao: /\[[A-Z0-9_]+\]/g };
  },
  addProseMirrorPlugins() {
    const padrao = this.options.padrao;
    return [
      new Plugin({
        props: {
          decorations(estado) {
            const decoracoes: Decoration[] = [];
            estado.doc.descendants((no, posicao) => {
              if (!no.isText || no.text === undefined) return;
              for (const m of no.text.matchAll(padrao)) {
                const inicio = posicao + (m.index ?? 0);
                decoracoes.push(
                  Decoration.inline(inicio, inicio + m[0].length, {
                    class: 'variavel-de-contrato',
                  }),
                );
              }
            });
            return DecorationSet.create(estado.doc, decoracoes);
          },
        },
      }),
    ];
  },
});

/** As extensões de cada modo — as mesmas da tela e da padronização sem tela. */
export function extensoesDoEditor(modo: ModoDoEditor, placeholder?: string): Extensions {
  return [
    StarterKit.configure({
      link:
        modo === 'email'
          ? { openOnClick: false, autolink: true, HTMLAttributes: { rel: null, target: null } }
          : false,
    }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    TableKit.configure({ table: { resizable: false } }),
    Placeholder.configure({
      placeholder:
        placeholder ??
        (modo === 'email' ? 'Escreva o e-mail aqui…' : 'Escreva o contrato aqui, como no Word…'),
    }),
    DestaqueDeVariaveis.configure({
      padrao: modo === 'email' ? /\{\{\{?[^{}]+\}\}\}?/g : /\[[A-Z0-9_]+\]/g,
    }),
  ];
}

/**
 * O HTML como o editor o guardaria — sem abrir o editor na tela.
 *
 * É a padronização da importação dos e-mails do Postmark: cada um passa por
 * aqui antes de ser gravado, e o que fica salvo é exatamente o que a pessoa vê
 * ao abrir.
 */
export function padronizarHtml(html: string, modo: ModoDoEditor): string {
  const editor = new EditorSemTela({ extensions: extensoesDoEditor(modo), content: html });
  const padronizado = editor.getHTML();
  editor.destroy();
  return padronizado;
}

export const EditorDeContrato = forwardRef<EditorDeContratoHandle, PropsEditorDeContrato>(
  function EditorDeContrato(props, ref): JSX.Element {
    const { value, onChange } = props;
    const modo = props.modo ?? 'contrato';

    const editor = useEditor({
      extensions: extensoesDoEditor(modo, props.placeholder),
      content: value,
      onUpdate: ({ editor: e }) => onChange(e.getHTML()),
      editorProps: { attributes: { spellcheck: 'true' } },
    });

    // HTML de fora para dentro — só quando de fato difere.
    useEffect(() => {
      if (editor === null) return;
      if (value !== editor.getHTML()) {
        editor.commands.setContent(value, { emitUpdate: false });
      }
    }, [editor, value]);

    useImperativeHandle(
      ref,
      () => ({
        inserir: (texto) => {
          editor?.chain().focus().insertContent(texto).run();
        },
      }),
      [editor],
    );

    if (editor === null) return <></>;

    return (
      <div
        className={juntarClasses(
          'editor-de-contrato flex flex-col overflow-hidden rounded-painel border border-borda bg-superficie-elevada',
          modo === 'email' && 'editor-de-email',
          props.className,
        )}
      >
        <Barra
          editor={editor}
          modo={modo}
          variaveis={
            props.variaveis ??
            (props.tokens ?? []).map((t) => ({ texto: `[${t}]`, rotulo: `[${t}]` }))
          }
        />
        {/* A folha: largura de página (ou de e-mail), fundo claro, como um documento aberto. */}
        <div className="overflow-y-auto bg-superficie-base px-4 py-6 sm:px-8">
          <div
            className={juntarClasses(
              'mx-auto w-full rounded-card border border-borda bg-white text-neutral-900 shadow-cartao dark:bg-neutral-50',
              modo === 'email'
                ? 'max-w-[600px] px-6 py-8 sm:px-8'
                : 'max-w-[21cm] px-8 py-10 sm:px-14 sm:py-14',
            )}
          >
            <EditorContent editor={editor} />
          </div>
        </div>
      </div>
    );
  },
);

/* ─── Barra de ferramentas ────────────────────────────────────────────────── */

function Barra(props: {
  readonly editor: Editor;
  readonly modo: ModoDoEditor;
  readonly variaveis: readonly VariavelDoEditor[];
}): JSX.Element {
  const { editor } = props;
  const dialogo = useDialogo();
  // Re-render a cada transação, para os botões refletirem o que está ativo.
  const [, forcar] = useState(0);
  useEffect(() => {
    const atualizar = (): void => forcar((n) => n + 1);
    editor.on('transaction', atualizar);
    editor.on('selectionUpdate', atualizar);
    return () => {
      editor.off('transaction', atualizar);
      editor.off('selectionUpdate', atualizar);
    };
  }, [editor]);

  const bloco = editor.isActive('heading', { level: 1 })
    ? 'h1'
    : editor.isActive('heading', { level: 2 })
      ? 'h2'
      : editor.isActive('heading', { level: 3 })
        ? 'h3'
        : 'p';

  return (
    <div
      role="toolbar"
      aria-label={props.modo === 'email' ? 'Formatação do e-mail' : 'Formatação do contrato'}
      className="sticky top-0 z-10 flex flex-wrap items-center gap-1 border-b border-borda bg-superficie-elevada px-2 py-1.5"
    >
      <Grupo>
        <Botao
          rotulo="Desfazer"
          onClick={() => editor.chain().focus().undo().run()}
          desabilitado={!editor.can().undo()}
        >
          ↶
        </Botao>
        <Botao
          rotulo="Refazer"
          onClick={() => editor.chain().focus().redo().run()}
          desabilitado={!editor.can().redo()}
        >
          ↷
        </Botao>
      </Grupo>

      <Grupo>
        <select
          aria-label="Estilo do parágrafo"
          value={bloco}
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'p') editor.chain().focus().setParagraph().run();
            else
              editor
                .chain()
                .focus()
                .setHeading({ level: Number(v.slice(1)) as 1 | 2 | 3 })
                .run();
          }}
          className="h-8 rounded-card border border-borda bg-superficie-base px-2 text-sm text-conteudo-primario"
        >
          <option value="p">Parágrafo</option>
          <option value="h1">Título</option>
          <option value="h2">{props.modo === 'email' ? 'Subtítulo' : 'Cláusula'}</option>
          <option value="h3">{props.modo === 'email' ? 'Tópico' : 'Subtítulo'}</option>
        </select>
      </Grupo>

      <Grupo>
        <Botao
          rotulo="Negrito"
          ativo={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <span className="font-bold">N</span>
        </Botao>
        <Botao
          rotulo="Itálico"
          ativo={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <span className="italic">I</span>
        </Botao>
        <Botao
          rotulo="Sublinhado"
          ativo={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <span className="underline">S</span>
        </Botao>
        {props.modo === 'email' && (
          <Botao
            rotulo={editor.isActive('link') ? 'Editar ou tirar o link' : 'Link'}
            ativo={editor.isActive('link')}
            onClick={async () => {
              const atual = (editor.getAttributes('link').href as string | undefined) ?? '';
              const endereco = await dialogo.pedirTexto({
                titulo: atual === '' ? 'Pôr um link' : 'Editar o link',
                descricao:
                  'Pode ser um endereço (https://…) ou uma variável, como {{url}}. Vazio tira o link.',
                rotulo: 'Endereço',
                valorInicial: atual,
              });
              if (endereco === null) return;
              if (endereco.trim() === '') {
                editor.chain().focus().extendMarkRange('link').unsetLink().run();
              } else {
                editor
                  .chain()
                  .focus()
                  .extendMarkRange('link')
                  .setLink({ href: endereco.trim() })
                  .run();
              }
            }}
          >
            <span className="underline decoration-2">🔗</span>
          </Botao>
        )}
      </Grupo>

      <Grupo>
        {(
          [
            ['left', 'Alinhar à esquerda', '≡'],
            ['center', 'Centralizar', '☰'],
            ['right', 'Alinhar à direita', '≡'],
            ['justify', 'Justificar', '☰'],
          ] as const
        ).map(([alinhamento, rotulo]) => (
          <Botao
            key={alinhamento}
            rotulo={rotulo}
            ativo={editor.isActive({ textAlign: alinhamento })}
            onClick={() => editor.chain().focus().setTextAlign(alinhamento).run()}
          >
            <IconeAlinhamento tipo={alinhamento} />
          </Botao>
        ))}
      </Grupo>

      <Grupo>
        <Botao
          rotulo="Lista com marcadores"
          ativo={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          •≡
        </Botao>
        <Botao
          rotulo="Lista numerada"
          ativo={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          1≡
        </Botao>
        <Botao
          rotulo="Linha separadora"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          —
        </Botao>
      </Grupo>

      <Grupo>
        {editor.isActive('table') ? (
          <>
            <Botao rotulo="Linha abaixo" onClick={() => editor.chain().focus().addRowAfter().run()}>
              +linha
            </Botao>
            <Botao
              rotulo="Coluna à direita"
              onClick={() => editor.chain().focus().addColumnAfter().run()}
            >
              +coluna
            </Botao>
            <Botao rotulo="Remover linha" onClick={() => editor.chain().focus().deleteRow().run()}>
              −linha
            </Botao>
            <Botao
              rotulo="Remover coluna"
              onClick={() => editor.chain().focus().deleteColumn().run()}
            >
              −coluna
            </Botao>
            <Botao
              rotulo="Remover tabela"
              onClick={() => editor.chain().focus().deleteTable().run()}
            >
              ×tabela
            </Botao>
          </>
        ) : (
          <Botao
            rotulo="Inserir tabela"
            onClick={() =>
              editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
            }
          >
            ⊞ Tabela
          </Botao>
        )}
      </Grupo>

      <Grupo>
        <select
          aria-label="Inserir variável"
          value=""
          onChange={(e) => {
            if (e.target.value === '') return;
            editor.chain().focus().insertContent(e.target.value).run();
          }}
          className="h-8 max-w-[14rem] rounded-card border border-acento-ciano/50 bg-superficie-base px-2 text-sm text-acento-ciano"
        >
          <option value="">Inserir variável…</option>
          {props.variaveis.map((v) => (
            <option key={v.texto} value={v.texto}>
              {v.rotulo}
            </option>
          ))}
        </select>
      </Grupo>
    </div>
  );
}

function Grupo(props: { readonly children: ReactNode }): JSX.Element {
  return (
    <span className="flex items-center gap-0.5 border-r border-borda pr-1 last:border-0">
      {props.children}
    </span>
  );
}

function Botao(props: {
  readonly rotulo: string;
  readonly ativo?: boolean;
  readonly desabilitado?: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      title={props.rotulo}
      aria-label={props.rotulo}
      aria-pressed={props.ativo}
      disabled={props.desabilitado}
      // `onMouseDown` com preventDefault: o clique não tira o foco (e a
      // seleção) do editor — senão o negrito se aplica a lugar nenhum.
      onMouseDown={(e) => e.preventDefault()}
      onClick={props.onClick}
      className={juntarClasses(
        'inline-flex h-8 min-w-8 items-center justify-center rounded-card px-1.5 text-sm transition-colors disabled:opacity-40',
        props.ativo === true
          ? 'bg-acento-ciano/15 text-acento-ciano'
          : 'text-conteudo-primario hover:bg-superficie-vidro',
      )}
    >
      {props.children}
    </button>
  );
}

function IconeAlinhamento(props: {
  readonly tipo: 'left' | 'center' | 'right' | 'justify';
}): JSX.Element {
  const linhas: Record<typeof props.tipo, readonly [number, number][]> = {
    left: [
      [3, 14],
      [3, 10],
      [3, 14],
      [3, 8],
    ],
    center: [
      [3, 14],
      [5, 10],
      [3, 14],
      [6, 8],
    ],
    right: [
      [3, 14],
      [7, 10],
      [3, 14],
      [9, 8],
    ],
    justify: [
      [3, 14],
      [3, 14],
      [3, 14],
      [3, 14],
    ],
  };
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4" aria-hidden="true" fill="currentColor">
      {linhas[props.tipo].map(([x, largura], i) => (
        <rect key={i} x={x} y={3 + i * 4} width={largura} height="1.6" rx="0.8" />
      ))}
    </svg>
  );
}
